import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { useDemo, useAppMode } from '../context/DemoContext';
import BackButton from '../components/BackButton';
import { 
  Gamepad2, 
  Lock, 
  Hammer, 
  Coins,
  Trophy,
  Target,
  Play,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Skull,
  Crosshair,
  Zap,
  Shield
} from 'lucide-react';
// EpicTitle removed — using custom molten title

import SafeCracker from '../components/games/SafeCracker';
import WhackAGremlin from '../components/games/WhackAGremlin';
import SlotMachine from '../components/games/SlotMachine';
import PricePrediction from '../components/games/PricePrediction';
import TheDescent from '../components/games/TheDescent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ARCADE_LOCKED = false;

const ARCADE_GAMES = [
  {
    id: 'safe-cracker',
    name: 'Safe Cracker',
    tagline: 'Breach 3 vault layers before the alarm triggers.',
    reward: '+20 SP',
    icon: Lock,
    component: SafeCracker,
    color: '#00ff41',
    bg: '/images/arcade/safe_cracker_bg.png',
  },
  {
    id: 'whack-a-gremlin',
    name: 'Snatch-a-Bag',
    tagline: 'Grab the loot bags. Dodge the pipe bombs.',
    reward: '+15 SP',
    icon: Hammer,
    component: WhackAGremlin,
    color: '#00ff41',
    bg: '/images/arcade/snatch_bag_bg.png',
  },
  {
    id: 'slot-machine',
    name: 'Fee Scrambler',
    tagline: 'Intercept the fee reels. Time your stops.',
    reward: '+10 SP',
    icon: Coins,
    component: SlotMachine,
    color: '#00ff41',
    bg: '/images/arcade/fee_scrambler_bg.png',
  },
  {
    id: 'price-prediction',
    name: 'Price Oracle',
    tagline: 'Read the charts. Predict the next candle.',
    reward: '+10 SP',
    icon: Crosshair,
    component: PricePrediction,
    color: '#00ff41',
    bg: '/images/arcade/price_oracle_bg.png',
  },
];

const DESCENT_GAME = {
  id: 'the-descent',
  name: 'The Descent',
  tagline: 'Ten floors. Each deeper than the last. Only the worthy survive.',
  reward: 'UP TO +500 SP',
  icon: Skull,
  component: TheDescent,
};

const GAMES = [...ARCADE_GAMES, DESCENT_GAME];

/* ═══════════════════════════════════════════
   GAME CARD — Standard Arcade (4 side cards)
   ═══════════════════════════════════════════ */
const ArcadeGameCard = ({ game, onClick, index, side }) => {
  const Icon = game.icon;
  const enterDir = side === 'left' ? -80 : 80;

  return (
    <motion.button
      initial={{ opacity: 0, x: enterDir }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.3 + index * 0.12, type: 'spring', stiffness: 120, damping: 18 }}
      onClick={onClick}
      className="group relative w-full h-full flex flex-col items-start justify-between p-5 lg:p-6 overflow-hidden cursor-pointer text-left"
      style={{
        background: 'rgba(10,10,10,0.6)',
        border: '1px solid rgba(255,140,0,0.12)',
        backdropFilter: 'blur(12px)',
        boxShadow: 'inset 0 0 20px rgba(255,69,0,0.03)',
        transition: 'border-color 0.3s, box-shadow 0.3s, transform 0.3s',
      }}
      whileHover={{
        y: -4,
        borderColor: 'rgba(255,140,0,0.5)',
        boxShadow: '0 0 25px rgba(255,69,0,0.15), inset 0 0 30px rgba(255,69,0,0.08)',
      }}
      whileTap={{ scale: 0.97 }}
      data-testid={`game-card-${game.id}`}
    >
      {/* Corner brackets */}
      <div className="absolute top-0 left-0 w-4 h-4 border-t border-l border-orange-500/30 group-hover:border-orange-400/60 transition-colors z-20" />
      <div className="absolute top-0 right-0 w-4 h-4 border-t border-r border-orange-500/30 group-hover:border-orange-400/60 transition-colors z-20" />
      <div className="absolute bottom-0 left-0 w-4 h-4 border-b border-l border-orange-500/30 group-hover:border-orange-400/60 transition-colors z-20" />
      <div className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-orange-500/30 group-hover:border-orange-400/60 transition-colors z-20" />

      {/* Background artwork */}
      {game.bg && (
        <div className="absolute inset-0 pointer-events-none">
          <img
            src={game.bg}
            alt=""
            className="w-full h-full object-cover opacity-20 group-hover:opacity-40 transition-opacity duration-700"
            style={{ filter: 'saturate(1.2) brightness(0.8)' }}
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,10,10,0.3) 0%, rgba(10,10,10,0.7) 60%, rgba(10,10,10,0.95) 100%)' }} />
        </div>
      )}

      {/* Hover sweep */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(135deg, transparent 40%, rgba(255,140,0,0.06) 50%, transparent 60%)', backgroundSize: '300% 300%' }}
        animate={{ backgroundPosition: ['0% 0%', '100% 100%'] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
      />

      {/* Top row: Icon + Reward */}
      <div className="flex items-start justify-between w-full relative z-10 mb-auto">
        <div className="p-2.5 rounded-lg" style={{ background: 'rgba(255,140,0,0.06)', border: '1px solid rgba(255,140,0,0.12)' }}>
          <Icon className="w-6 h-6 group-hover:drop-shadow-[0_0_8px_rgba(255,140,0,0.8)] transition-all" style={{ color: 'rgba(255,140,0,0.6)' }} />
        </div>
        <div className="px-2.5 py-1" style={{
          background: 'linear-gradient(135deg, #ffd700, #ff8c00)',
          color: '#000',
          fontFamily: "'Impact', 'Arial Black', sans-serif",
          fontSize: '14px',
          transform: 'skewX(-12deg)',
          boxShadow: '0 0 10px rgba(255,140,0,0.4)',
        }}>
          {game.reward}
        </div>
      </div>

      {/* Title + Tagline */}
      <div className="relative z-10 mt-3">
        <h3 className="text-xl lg:text-2xl uppercase tracking-wide transition-colors"
          style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.06em', fontWeight: 700,
            background: 'linear-gradient(180deg, #ffd700, #ff8c00, #cc5500)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
          {game.name}
        </h3>
        <p className="text-[11px] lg:text-xs mt-1 leading-snug"
          style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,160,64,0.5)', fontWeight: 700 }}>
          {game.tagline}
        </p>
      </div>

      {/* Bottom: PLAY indicator */}
      <div className="flex items-center gap-1.5 mt-3 relative z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <Play className="w-3 h-3" style={{ color: '#ff8c00' }} />
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold" style={{ fontFamily: "'Courier New', monospace", color: '#ff8c00' }}>
          Launch
        </span>
      </div>
    </motion.button>
  );
};

/* ═══════════════════════════════════════════
   BOSS CARD — The Descent (Center)
   ═══════════════════════════════════════════ */
const BossCard = ({ onClick }) => {
  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.5, type: 'spring', stiffness: 80, damping: 15 }}
      onClick={onClick}
      className="group relative w-full h-full flex flex-col items-center justify-center overflow-hidden cursor-pointer"
      style={{
        background: 'linear-gradient(180deg, rgba(16,0,0,0.95) 0%, rgba(30,0,0,0.9) 100%)',
        border: '2px solid rgba(239,68,68,0.3)',
        backdropFilter: 'blur(16px)',
        boxShadow: '0 0 40px rgba(239,68,68,0.1), inset 0 0 50px rgba(239,68,68,0.06)',
        transition: 'border-color 0.4s, box-shadow 0.4s',
      }}
      whileHover={{
        y: -6,
        borderColor: 'rgba(239,68,68,0.7)',
        boxShadow: '0 0 80px rgba(239,68,68,0.3), inset 0 0 60px rgba(239,68,68,0.15)',
      }}
      whileTap={{ scale: 0.98 }}
      data-testid="game-card-the-descent"
    >
      {/* Background artwork */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="/images/arcade/descent_abyss.png"
          alt=""
          className="w-full h-full object-cover opacity-30 group-hover:opacity-50 transition-opacity duration-700"
          style={{ mixBlendMode: 'screen', filter: 'saturate(1.4) hue-rotate(15deg)' }}
        />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, transparent 20%, rgba(16,0,0,0.85) 75%)' }} />
      </div>

      {/* Animated radial pulse */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, rgba(239,68,68,0.15) 0%, transparent 60%)' }}
        animate={{ opacity: [0.3, 0.8, 0.3], scale: [0.95, 1.05, 0.95] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Vertical scanline sweep */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(239,68,68,0.08) 50%, transparent 100%)', width: '30%' }}
        animate={{ left: ['-30%', '130%'] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'linear', repeatDelay: 2 }}
      />

      {/* Danger striped top edge */}
      <div className="absolute top-0 left-0 right-0 h-1"
        style={{ background: 'repeating-linear-gradient(90deg, #ef4444 0px, #ef4444 8px, transparent 8px, transparent 16px)', opacity: 0.5 }} />

      {/* Reward badge */}
      <div className="absolute top-5 right-5 z-20 px-3.5 py-1.5"
        style={{
          background: '#ef4444',
          color: '#000',
          fontFamily: "'Impact', 'Arial Black', sans-serif",
          fontSize: '16px',
          transform: 'skewX(-12deg)',
          boxShadow: '0 0 20px rgba(239,68,68,0.6)',
          border: '1px solid rgba(239,68,68,0.7)',
        }}>
        UP TO +500 SP
      </div>

      {/* Skull artwork */}
      <motion.div
        className="relative z-10 mb-3"
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      >
        <img
          src="/images/arcade/descent_skull.png"
          alt="The Descent"
          className="w-20 h-20 lg:w-28 lg:h-28 object-contain group-hover:scale-110 transition-transform duration-500"
          style={{ filter: 'drop-shadow(0 0 25px rgba(239,68,68,0.8)) drop-shadow(0 0 50px rgba(239,68,68,0.3))' }}
        />
      </motion.div>

      {/* Title with flicker */}
      <motion.h2
        className="relative z-10 text-5xl lg:text-7xl uppercase text-center"
        style={{
          fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
          color: '#f87171',
          textShadow: '0 0 30px rgba(239,68,68,0.6), 0 0 60px rgba(239,68,68,0.2)',
          letterSpacing: '0.08em',
          fontWeight: 700,
        }}
        animate={{ opacity: [1, 1, 0.3, 1, 1, 1, 0.4, 1] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
      >
        THE DESCENT
      </motion.h2>

      {/* Tagline */}
      <p className="relative z-10 mt-3 text-sm lg:text-base text-center max-w-[80%] leading-relaxed"
        style={{ fontFamily: "'Courier New', monospace", color: 'rgba(248,113,113,0.7)', fontWeight: 700 }}>
        Ten floors. Each deeper than the last. Only the worthy survive.
      </p>

      {/* Floor indicators */}
      <div className="relative z-10 flex items-center gap-1.5 mt-5">
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            className="w-2 h-2 rounded-full"
            style={{
              background: i < 3 ? '#fca5a5' : i < 7 ? '#ef4444' : '#dc2626',
              boxShadow: `0 0 ${4 + i}px rgba(239,68,68,0.6)`,
            }}
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 2, delay: i * 0.15, repeat: Infinity }}
          />
        ))}
      </div>

      {/* ENTER prompt */}
      <motion.div
        className="relative z-10 flex items-center gap-2 mt-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        animate={{ y: [0, -3, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <span className="text-xs tracking-[0.3em] uppercase font-bold" style={{ fontFamily: "'Courier New', monospace", color: '#ef4444' }}>
          Enter if you dare
        </span>
      </motion.div>

      {/* Bottom danger stripe */}
      <div className="absolute bottom-0 left-0 right-0 h-1"
        style={{ background: 'repeating-linear-gradient(90deg, #ef4444 0px, #ef4444 8px, transparent 8px, transparent 16px)', opacity: 0.5 }} />
    </motion.button>
  );
};


/* ═══════════════════════════════════════════
   LOCKED GAME CARD (pre-launch)
   ═══════════════════════════════════════════ */
const LockedGameCard = ({ game, index }) => {
  const Icon = game.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.8 + index * 0.15, duration: 0.5 }}
      className="group relative w-full p-5 rounded-xl overflow-hidden cursor-not-allowed"
      style={{ background: 'rgba(255,255,255,0.015)', border: '1px solid rgba(255,255,255,0.06)' }}
      data-testid={`locked-game-${game.id}`}
    >
      <motion.div className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(transparent 50%, rgba(168, 85, 247, 0.03) 50%)', backgroundSize: '100% 4px' }} />
      <div className="flex items-center justify-between relative">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-white/[0.03] flex items-center justify-center relative">
            <Icon className="w-5 h-5 text-white/15" />
            <Lock className="w-3 h-3 text-white/20 absolute -bottom-0.5 -right-0.5" />
          </div>
          <div>
            <h3 className="font-semibold text-white/25">{game.name}</h3>
            <p className="text-sm text-white/10">{game.description || game.tagline}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] tracking-widest text-white/15 font-bold uppercase">Locked</span>
          <Lock className="w-3.5 h-3.5 text-white/15" />
        </div>
      </div>
    </motion.div>
  );
};


/* ═══════════════════════════════════════════
   LOCKED ARCADE (Full locked page pre-launch)
   ═══════════════════════════════════════════ */
const LockedArcade = () => {
  const navigate = useNavigate();
  const LockedPage = require('../components/LockedPage').default;
  return (
    <LockedPage
      title="THE HEIST"
      subtitle="Four operations. One target: steal every fee in sight."
      lore="The sewer arcade houses four skill-based games. Every play earns in-game fees and Season Points. No real money wagered — just your reflexes, your timing, and your greed. The pipes crack open at $GFG launch."
      statusLabel="Access Restricted"
      tickerText="Intercepting network fees..."
      theme={{ primary: '#ff8c00', glow: '#ff6b2b', bg: '#120600' }}
      particles="embers"
      features={[
        { icon: Lock, title: 'Safe Cracker', desc: 'Breach 3 vault layers before the alarm triggers. +20 SP' },
        { icon: Hammer, title: 'Snatch-a-Bag', desc: 'Grab the loot bags. Dodge the pipe bombs. +15 SP' },
        { icon: Coins, title: 'Fee Scrambler', desc: 'Intercept the fee reels. Time your stops. +10 SP' },
        { icon: Skull, title: 'The Descent', desc: '10 progressive risk floors. Up to +600 SP per run.' },
      ]}
      bottomCTA={
        <div className="flex items-center justify-between gap-4 px-5 py-3"
          style={{ background: 'rgba(255,140,0,0.06)', border: '1px solid rgba(255,140,0,0.15)' }}>
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4" style={{ color: '#ffd700' }} />
            <span className="text-[10px] tracking-wider uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,180,120,0.7)' }}>Season Rewards</span>
            <span className="text-sm font-bold" style={{ fontFamily: "'Impact', sans-serif", color: '#ffd700' }}>Earn SP, climb the leaderboard</span>
          </div>
          <button onClick={() => window.open('https://discord.gg/3wH7NaVXxP', '_blank')} className="px-4 py-1.5 text-xs font-bold tracking-wider uppercase transition-all hover:scale-105"
            style={{ fontFamily: "'Cinzel', serif", background: 'rgba(255,140,0,0.12)', border: '1px solid rgba(255,140,0,0.3)', color: '#ff8c00' }}>
            Discord
          </button>
        </div>
      }
    />
  );
};


/* ═══════════════════════════════════════════
   MAIN ARCADE PAGE
   ═══════════════════════════════════════════ */
export default function ArcadePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { publicKey, connected: walletConnected } = useWallet();
  const { isDemo, demoWallet } = useDemo();
  const { isLive } = useAppMode();
  const connected = walletConnected || isDemo;
  const walletAddress = publicKey?.toBase58() || (isDemo ? demoWallet : undefined);
  
  const [selectedGame, setSelectedGame] = useState(null);
  const [communityFees, setCommunityFees] = useState(0);
  const [communityGames, setCommunityGames] = useState(0);
  const [communityPlayers, setCommunityPlayers] = useState(0);
  const [myBalance, setMyBalance] = useState(0);
  const [myTotalFees, setMyTotalFees] = useState(0);
  const [mySp, setMySp] = useState(0);
  const { features, loading: featuresLoading } = useFeatureFlags();
  const [showExitWarning, setShowExitWarning] = useState(false);
  const [pendingExitAction, setPendingExitAction] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const game = searchParams.get('game');
    if (game) {
      if (game === 'the-descent' && isMobile) {
        setSearchParams({}, { replace: true });
        return;
      }
      setSelectedGame(game);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, isMobile]);

  useEffect(() => {
    const fetchCommunityStats = async () => {
      try {
        const res = await fetch(`${API_URL}/api/arcade/stats`);
        if (res.ok) {
          const data = await res.json();
          setCommunityFees(data.total_fees_stolen || 0);
          setCommunityGames(data.total_games_played || 0);
          setCommunityPlayers(data.total_players || 0);
        }
      } catch (err) { console.error('Failed to fetch community stats:', err); }
    };
    fetchCommunityStats();
    const interval = setInterval(fetchCommunityStats, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!walletAddress) return;
    const fetchMyStats = async () => {
      try {
        const [balRes, statsRes, spRes] = await Promise.all([
          fetch(`${API_URL}/api/slots/user/${walletAddress}`),
          fetch(`${API_URL}/api/arcade/stats/${walletAddress}`),
          fetch(`${API_URL}/api/seasons/sp-breakdown/${walletAddress}`),
        ]);
        if (balRes.ok) { const d = await balRes.json(); setMyBalance(d.fee_balance || 0); }
        if (statsRes.ok) { const d = await statsRes.json(); setMyTotalFees(d.total_fees || 0); }
        if (spRes.ok) { const d = await spRes.json(); setMySp(d.breakdown?.total_sp || 0); }
      } catch (err) { /* silent */ }
    };
    fetchMyStats();
    const interval = setInterval(fetchMyStats, 15000);
    return () => clearInterval(interval);
  }, [walletAddress]);

  useEffect(() => {
    if (!selectedGame) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [selectedGame]);

  const tryExit = useCallback((action) => {
    if (selectedGame) {
      setPendingExitAction(() => action);
      setShowExitWarning(true);
    } else {
      action();
    }
  }, [selectedGame]);

  useEffect(() => {
    window.__gfgGameActive = !!selectedGame;
    if (selectedGame) {
      document.body.classList.add('game-active');
    } else {
      document.body.classList.remove('game-active');
    }
    const onSwipeBack = () => {
      if (selectedGame) {
        tryExit(() => setSelectedGame(null));
      }
    };
    window.addEventListener('gfg-swipe-back', onSwipeBack);
    return () => {
      window.__gfgGameActive = false;
      document.body.classList.remove('game-active');
      window.removeEventListener('gfg-swipe-back', onSwipeBack);
    };
  }, [selectedGame, tryExit]);

  const confirmExit = () => {
    setShowExitWarning(false);
    if (pendingExitAction) pendingExitAction();
    setPendingExitAction(null);
  };

  const cancelExit = () => {
    setShowExitWarning(false);
    setPendingExitAction(null);
  };

  if (featuresLoading) return <div className="h-screen bg-[#0a0a0a] flex items-center justify-center"><div className="w-5 h-5 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" /></div>;
  if (!features.arcade && !isDemo && !isLive) return <LockedArcade />;

  if (isMobile) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col items-center justify-center px-6 relative overflow-hidden" data-testid="arcade-mobile-block">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.4), transparent 70%)', filter: 'blur(80px)' }} />
        <div className="relative z-10 text-center max-w-sm">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center"
            style={{ background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.15)' }}>
            <Gamepad2 className="w-10 h-10 text-purple-400/60" />
          </div>
          <h1 className="text-2xl font-black tracking-wide mb-3" style={{ fontFamily: "'Bebas Neue', cursive" }}>DESKTOP ONLY</h1>
          <p className="text-white/40 text-sm leading-relaxed mb-8">
            The GFG Arcade requires a desktop browser for maximum performance. Our canvas-based games need the power of a full screen.
          </p>
          <BackButton to="/" fixed={false} />
        </div>
      </div>
    );
  }

  const handleGameWin = async () => {
    try {
      const res = await fetch(`${API_URL}/api/arcade/stats`);
      if (res.ok) {
        const data = await res.json();
        setCommunityFees(data.total_fees_stolen || 0);
        setCommunityGames(data.total_games_played || 0);
        setCommunityPlayers(data.total_players || 0);
      }
    } catch (err) { /* silent */ }
  };

  const backToSelection = () => setSelectedGame(null);
  const GameComponent = selectedGame ? GAMES.find(g => g.id === selectedGame)?.component : null;

  return (
    <div className="h-screen w-screen bg-[#0a0a0a] text-white overflow-hidden relative">

      {/* ═══ LIVE MODE NOTICE — gameplay writes to Solana Devnet ═══ */}
      {isLive && !selectedGame && (
        <div
          className="fixed top-3 left-1/2 -translate-x-1/2 z-[80] px-3 py-1.5 flex items-center gap-2 rounded-full"
          data-testid="arcade-live-notice"
          style={{
            background: 'rgba(8,18,28,0.92)',
            border: '1px solid rgba(125,208,255,0.45)',
            boxShadow: '0 0 14px rgba(125,208,255,0.16)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <span
            className="inline-block rounded-full"
            style={{
              width: 6, height: 6,
              background: '#7dd0ff',
              boxShadow: '0 0 6px #7dd0ff',
              animation: 'gfg-arcade-pulse 1.4s ease-in-out infinite',
            }}
          />
          <span
            className="text-[9.5px] tracking-[0.22em] uppercase font-bold"
            style={{ color: '#a8def7', fontFamily: "'Cinzel', serif" }}
          >DEVNET · LIVE</span>
          <span
            className="text-[9.5px]"
            style={{ color: 'rgba(168,222,247,0.7)', fontFamily: "'Courier New', monospace" }}
          >every play notarized on-chain · view tx on Solscan</span>
          <style>{`
            @keyframes gfg-arcade-pulse { 0%,100% { opacity: .55; } 50% { opacity: 1; } }
          `}</style>
        </div>
      )}

      {/* ═══ CRT Scanline Overlay ═══ */}
      <div className="fixed inset-0 z-[60] pointer-events-none mix-blend-overlay"
        style={{
          background: 'linear-gradient(rgba(18,16,16,0) 50%, rgba(0,0,0,0.25) 50%), linear-gradient(90deg, rgba(255,0,0,0.04), rgba(0,255,0,0.015), rgba(0,0,255,0.04))',
          backgroundSize: '100% 4px, 3px 100%',
          opacity: 0.3,
        }} />

      {/* ═══ Vignette ═══ */}
      <div className="fixed inset-0 z-[55] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.7) 100%)' }} />

      {/* ═══ Animated Fog/Mist Background ═══ */}
      {!selectedGame && (
        <div className="fixed inset-0 z-[1] pointer-events-none overflow-hidden">
          {/* Large slow fog banks */}
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={`fog-${i}`}
              className="absolute"
              style={{
                width: `${500 + Math.random() * 400}px`,
                height: `${200 + Math.random() * 150}px`,
                top: `${10 + i * 18}%`,
                left: '-30%',
                background: `radial-gradient(ellipse, rgba(${i % 2 === 0 ? '0,255,65' : '168,85,247'},0.04) 0%, transparent 70%)`,
                filter: 'blur(40px)',
              }}
              animate={{
                x: ['0vw', '160vw'],
              }}
              transition={{
                duration: 30 + i * 8,
                delay: i * 4,
                repeat: Infinity,
                ease: 'linear',
              }}
            />
          ))}
          {/* Thin drifting mist wisps */}
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={`mist-${i}`}
              className="absolute"
              style={{
                width: `${300 + Math.random() * 200}px`,
                height: `${60 + Math.random() * 40}px`,
                top: `${5 + i * 12}%`,
                right: '-20%',
                background: `radial-gradient(ellipse, rgba(255,255,255,0.02) 0%, transparent 70%)`,
                filter: 'blur(20px)',
              }}
              animate={{
                x: ['0vw', '-160vw'],
                opacity: [0, 0.6, 0.4, 0],
              }}
              transition={{
                duration: 25 + i * 5,
                delay: i * 3 + 2,
                repeat: Infinity,
                ease: 'linear',
              }}
            />
          ))}
          {/* Rising sewer gas particles */}
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={`gas-${i}`}
              className="absolute rounded-full"
              style={{
                left: `${5 + i * 10}%`,
                bottom: '-30px',
                width: `${15 + Math.random() * 20}px`,
                height: `${15 + Math.random() * 20}px`,
                background: i % 3 === 0
                  ? 'radial-gradient(circle, rgba(255,69,0,0.2) 0%, transparent 70%)'
                  : i % 3 === 1
                  ? 'radial-gradient(circle, rgba(0,255,65,0.15) 0%, transparent 70%)'
                  : 'radial-gradient(circle, rgba(168,85,247,0.15) 0%, transparent 70%)',
                filter: 'blur(3px)',
              }}
              animate={{
                y: [0, -700],
                x: [0, Math.sin(i * 0.9) * 30],
                opacity: [0, 0.4, 0.15, 0],
              }}
              transition={{
                duration: 10 + Math.random() * 5,
                delay: i * 0.8,
                repeat: Infinity,
                ease: 'easeOut',
              }}
            />
          ))}
        </div>
      )}

      {/* ═══ Back Button ═══ */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => tryExit(selectedGame ? () => setSelectedGame(null) : () => navigate('/'))}
        className="fixed top-5 left-5 z-[100] group flex items-center gap-2 px-5 py-2.5 rounded-full overflow-hidden"
        style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(12px)', border: '1px solid rgba(168,85,247,0.15)' }}
        whileHover={{ scale: 1.05, boxShadow: '0 0 25px rgba(168,85,247,0.12)' }}
        whileTap={{ scale: 0.95 }}
        data-testid="back-button"
      >
        <motion.div className="absolute inset-[-1px] rounded-full pointer-events-none"
          style={{ background: 'conic-gradient(from 0deg, transparent, rgba(168,85,247,0.25), rgba(34,197,94,0.15), transparent)', WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 1.5px), black calc(100% - 1.5px))', mask: 'radial-gradient(farthest-side, transparent calc(100% - 1.5px), black calc(100% - 1.5px))' }}
          animate={{ rotate: [0, 360] }} transition={{ duration: 6, repeat: Infinity, ease: 'linear' }} />
        <motion.div className="relative" animate={{ x: [0, -2, 0] }} transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}>
          <ChevronLeft className="w-4 h-4" style={{ color: '#a855f7', filter: 'drop-shadow(0 0 4px rgba(168,85,247,0.4))' }} />
        </motion.div>
        <span className="relative text-sm font-mono font-bold tracking-wider" style={{ background: 'linear-gradient(90deg, #a855f7, #22c55e)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          {selectedGame ? 'Games' : 'Back'}
        </span>
      </motion.button>

      {/* ═══ Exit Warning Modal ═══ */}
      <AnimatePresence>
        {showExitWarning && (() => {
          const isDescent = selectedGame === 'the-descent';
          return (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(10px)' }}
            onClick={cancelExit}
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="relative max-w-lg w-full mx-4 p-8 overflow-hidden"
              style={{
                background: isDescent
                  ? 'linear-gradient(180deg, #0a0412 0%, #080210 100%)'
                  : 'linear-gradient(180deg, #0a0500 0%, #0d0300 100%)',
                border: isDescent
                  ? '1px solid rgba(192,38,211,0.4)'
                  : '1px solid rgba(255,69,0,0.4)',
                boxShadow: isDescent
                  ? '0 0 50px rgba(192,38,211,0.15), inset 0 0 20px rgba(192,38,211,0.08)'
                  : '0 0 50px rgba(255,69,0,0.15), inset 0 0 20px rgba(255,69,0,0.08)',
              }}
              onClick={(e) => e.stopPropagation()}
              data-testid="exit-warning-modal"
            >
              {/* Danger stripe top */}
              <div className="absolute top-0 left-0 right-0 h-2"
                style={{ background: isDescent
                  ? 'repeating-linear-gradient(45deg, #c026d3, #c026d3 10px, #0a0412 10px, #0a0412 20px)'
                  : 'repeating-linear-gradient(45deg, #ff4500, #ff4500 10px, #1a0800 10px, #1a0800 20px)' }} />

              <AlertTriangle className="w-12 h-12 mx-auto mb-4 mt-2" style={{
                color: isDescent ? '#c026d3' : '#ff4500',
                filter: isDescent ? 'drop-shadow(0 0 15px rgba(192,38,211,0.6))' : 'drop-shadow(0 0 15px rgba(255,69,0,0.6))',
              }} />
              <h3 className="text-3xl text-center mb-3 uppercase"
                style={{
                  fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
                  color: isDescent ? '#c084fc' : '#ff4500',
                  textShadow: isDescent
                    ? '0 0 20px rgba(192,38,211,0.5), 0 0 40px rgba(220,38,38,0.2)'
                    : '0 0 20px rgba(255,69,0,0.5), 0 0 40px rgba(255,69,0,0.2)',
                  letterSpacing: '0.08em', fontWeight: 700,
                }}>
                {isDescent ? 'Abandon The Abyss?' : 'Abort Heist?'}
              </h3>
              <p className="text-center mb-6 leading-relaxed"
                style={{
                  fontFamily: "'Courier New', monospace",
                  color: isDescent ? 'rgba(192,132,252,0.6)' : 'rgba(255,140,0,0.6)',
                  fontWeight: 700, fontSize: '14px',
                }}>
                {isDescent ? (
                  <>The abyss does not forgive retreat.<br />
                  <span style={{ color: 'rgba(220,38,38,0.9)' }}>Your entry fee and all progress will be lost.</span></>
                ) : (
                  <>Leaving now means losing all progress.<br />
                  <span style={{ color: 'rgba(255,69,0,0.9)' }}>Unclaimed fees cannot be recovered.</span></>
                )}
              </p>
              <div className="flex gap-4">
                <button onClick={cancelExit} data-testid="exit-cancel-btn"
                  className="flex-1 py-3.5 uppercase text-xl tracking-wider transition-all"
                  style={{
                    fontFamily: "'Cinzel', serif", fontWeight: 700,
                    background: isDescent ? 'rgba(192,38,211,0.06)' : 'rgba(255,215,0,0.06)',
                    border: isDescent ? '1px solid rgba(192,38,211,0.3)' : '1px solid rgba(255,215,0,0.3)',
                    color: isDescent ? '#c084fc' : '#ffd700',
                  }}>
                  Stay
                </button>
                <button onClick={confirmExit} data-testid="exit-confirm-btn"
                  className="flex-1 py-3.5 uppercase text-xl tracking-wider transition-all hover:bg-red-600/30"
                  style={{
                    fontFamily: "'Cinzel', serif", fontWeight: 700,
                    background: isDescent ? 'rgba(220,38,38,0.08)' : 'rgba(255,69,0,0.08)',
                    border: isDescent ? '1px solid rgba(220,38,38,0.4)' : '1px solid rgba(255,69,0,0.4)',
                    color: isDescent ? '#dc2626' : '#ff4500',
                  }}>
                  Leave
                </button>
              </div>
            </motion.div>
          </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* ═══ MAIN CONTENT ═══ */}
      <div className={`relative z-10 h-full ${selectedGame ? '' : 'flex flex-col'}`}>

        <AnimatePresence mode="popLayout">
          {!selectedGame ? (
            <motion.div
              key="selection"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col h-full"
            >
              {/* ═══ HEADER: Ancient Molten Title ═══ */}
              <div className="pt-4 pb-1 px-6 flex-shrink-0 relative text-center">
                {/* Ember particles */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                  {[...Array(14)].map((_, i) => (
                    <motion.div
                      key={`ember-${i}`}
                      className="absolute rounded-full"
                      style={{
                        left: `${30 + Math.random() * 40}%`,
                        bottom: '10%',
                        width: `${2 + Math.random() * 3}px`,
                        height: `${2 + Math.random() * 3}px`,
                        background: i % 3 === 0 ? '#ff6b2b' : i % 3 === 1 ? '#ff4500' : '#ffa040',
                        boxShadow: `0 0 ${4 + Math.random() * 6}px ${i % 2 === 0 ? 'rgba(255,107,43,0.8)' : 'rgba(255,69,0,0.6)'}`,
                      }}
                      animate={{
                        y: [0, -120 - Math.random() * 80],
                        x: [0, (Math.random() - 0.5) * 60],
                        opacity: [0, 1, 0.8, 0],
                        scale: [0.5, 1, 0.3],
                      }}
                      transition={{
                        duration: 2 + Math.random() * 1.5,
                        delay: i * 0.25,
                        repeat: Infinity,
                        ease: 'easeOut',
                      }}
                    />
                  ))}
                </div>

                {/* Subtitle */}
                <motion.p
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-[10px] uppercase tracking-[0.5em] mb-2"
                  style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,160,64,0.5)' }}
                >
                  Where Gremlins Get Rich
                </motion.p>

                {/* Main Title — molten stone */}
                <motion.h1
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ delay: 0.1, duration: 0.6, type: 'spring', stiffness: 100 }}
                  className="relative text-5xl md:text-6xl lg:text-7xl font-bold uppercase tracking-wider inline-block"
                  style={{ fontFamily: "'Cinzel Decorative', 'Cinzel', serif" }}
                  data-testid="heist-title"
                >
                  {/* Deep shadow layer */}
                  <span className="absolute inset-0" style={{
                    color: 'transparent',
                    WebkitTextStroke: '2px rgba(139,30,0,0.4)',
                    filter: 'blur(4px)',
                  }}>THE HEIST</span>

                  {/* Molten core text */}
                  <span className="relative" style={{
                    background: 'linear-gradient(180deg, #ffd700 0%, #ff8c00 20%, #ff4500 45%, #cc2200 65%, #8b1a00 85%, #4a0e00 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    filter: 'drop-shadow(0 0 20px rgba(255,69,0,0.5)) drop-shadow(0 2px 4px rgba(0,0,0,0.8))',
                  }}>THE HEIST</span>

                  {/* Heat shimmer overlay */}
                  <motion.span
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: 'linear-gradient(90deg, transparent 0%, rgba(255,215,0,0.35) 50%, transparent 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundSize: '200% 100%',
                    }}
                    animate={{ backgroundPosition: ['200% 0%', '-200% 0%'] }}
                    transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                  >THE HEIST</motion.span>

                  {/* Intermittent glow pulse */}
                  <motion.span
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: 'linear-gradient(180deg, #ffd700 0%, #ff4500 50%, #8b1a00 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                    animate={{ opacity: [0, 0.3, 0, 0, 0.2, 0] }}
                    transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                  >THE HEIST</motion.span>
                </motion.h1>

                {/* Description */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="mt-2 text-sm max-w-md mx-auto"
                  style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.35)' }}
                >
                  Pick your operation. Steal the fees. Climb the ranks.
                </motion.p>

                {/* Molten underline */}
                <motion.div
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: '40%', opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  className="mx-auto mt-3 h-[2px]"
                  style={{ background: 'linear-gradient(90deg, transparent, #ff4500, #ffd700, #ff4500, transparent)' }}
                />
              </div>

              {/* ═══ GAME GRID ═══ */}
              <div className="flex-1 px-4 lg:px-8 pb-2 min-h-0">
                <div className="grid grid-cols-12 grid-rows-2 gap-3 lg:gap-4 h-full max-h-full">
                  {/* Left column */}
                  <div className="col-span-3 row-start-1">
                    <ArcadeGameCard game={ARCADE_GAMES[0]} onClick={() => setSelectedGame(ARCADE_GAMES[0].id)} index={0} side="left" />
                  </div>
                  <div className="col-span-3 row-start-2">
                    <ArcadeGameCard game={ARCADE_GAMES[1]} onClick={() => setSelectedGame(ARCADE_GAMES[1].id)} index={1} side="left" />
                  </div>

                  {/* CENTER: The Descent */}
                  <div className="col-span-6 row-span-2">
                    <BossCard onClick={() => setSelectedGame('the-descent')} />
                  </div>

                  {/* Right column */}
                  <div className="col-span-3 row-start-1 col-start-10">
                    <ArcadeGameCard game={ARCADE_GAMES[2]} onClick={() => setSelectedGame(ARCADE_GAMES[2].id)} index={2} side="right" />
                  </div>
                  <div className="col-span-3 row-start-2 col-start-10">
                    <ArcadeGameCard game={ARCADE_GAMES[3]} onClick={() => setSelectedGame(ARCADE_GAMES[3].id)} index={3} side="right" />
                  </div>
                </div>
              </div>

              {/* ═══ STATS BAR — Compact Centered Molten ═══ */}
              <div className="flex-shrink-0 border-t px-6 py-2.5 flex items-center justify-center gap-8"
                style={{
                  borderColor: 'rgba(255,140,0,0.1)',
                  background: 'linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.95) 100%)',
                  backdropFilter: 'blur(12px)',
                }}>

                {/* Balance */}
                {connected && (
                  <div className="flex items-center gap-2" data-testid="stats-personal-balance">
                    <span className="text-[9px] tracking-[0.15em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,160,64,0.45)' }}>Balance</span>
                    <span className="text-base tracking-wider font-bold" style={{
                      fontFamily: "'Impact', 'Arial Black', sans-serif",
                      background: 'linear-gradient(90deg, #ffd700, #ff8c00)',
                      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                      filter: 'drop-shadow(0 0 4px rgba(255,140,0,0.3))',
                    }}>
                      {myBalance.toLocaleString('de-DE')}
                    </span>
                  </div>
                )}

                {/* Separator */}
                {connected && <div className="w-[1px] h-4" style={{ background: 'rgba(255,140,0,0.15)' }} />}

                {/* Fees Stolen */}
                <div className="flex items-center gap-2" data-testid="stats-community-fees">
                  <span className="text-[9px] tracking-[0.15em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,160,64,0.45)' }}>Fees</span>
                  <span className="text-base tracking-wider font-bold" style={{
                    fontFamily: "'Impact', 'Arial Black', sans-serif",
                    background: 'linear-gradient(90deg, #ffd700, #ff8c00)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    filter: 'drop-shadow(0 0 4px rgba(255,140,0,0.3))',
                  }}>
                    {connected ? myTotalFees.toLocaleString('de-DE') : communityFees.toLocaleString('de-DE')}
                  </span>
                </div>

                {/* Separator */}
                <div className="w-[1px] h-4" style={{ background: 'rgba(255,140,0,0.15)' }} />

                {/* SP */}
                <div className="flex items-center gap-2" data-testid="stats-sp">
                  <span className="text-[9px] tracking-[0.15em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,160,64,0.45)' }}>SP</span>
                  <span className="text-base tracking-wider font-bold" style={{
                    fontFamily: "'Impact', 'Arial Black', sans-serif",
                    background: 'linear-gradient(90deg, #ffd700, #ff8c00)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    filter: 'drop-shadow(0 0 4px rgba(255,140,0,0.3))',
                  }}>
                    {connected ? mySp.toLocaleString('de-DE') : communityGames.toLocaleString('de-DE')}
                  </span>
                </div>

                {/* Separator */}
                <div className="w-[1px] h-4" style={{ background: 'rgba(255,140,0,0.15)' }} />

                {/* Leaderboard */}
                <motion.button
                  onClick={() => navigate('/arcade/leaderboard')}
                  className="flex items-center gap-1.5 px-3 py-1.5 transition-all"
                  style={{ border: '1px solid rgba(255,140,0,0.15)', background: 'rgba(255,140,0,0.04)' }}
                  whileHover={{ borderColor: 'rgba(255,140,0,0.4)', boxShadow: '0 0 12px rgba(255,69,0,0.1)' }}
                  data-testid="btn-leaderboard"
                >
                  <Trophy className="w-3.5 h-3.5" style={{ color: 'rgba(255,160,64,0.6)' }} />
                  <span className="text-[10px] tracking-[0.12em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,160,64,0.6)' }}>
                    Leaderboard
                  </span>
                </motion.button>

                {/* Connect Wallet (if not connected) */}
                {!connected && (
                  <>
                    <div className="w-[1px] h-4" style={{ background: 'rgba(255,140,0,0.15)' }} />
                    <WalletMultiButton className="!bg-orange-500/8 !border !border-orange-500/20 !rounded-sm !py-1.5 !px-4 !text-[10px]" data-testid="btn-connect-wallet" />
                  </>
                )}
              </div>

              {/* Disclaimer */}
              <div className="flex-shrink-0 py-1 px-4 text-center" style={{ background: 'rgba(0,0,0,0.9)' }}>
                <p className="text-white/10 text-[8px]" style={{ fontFamily: "'Courier New', monospace" }}>
                  All games use in-game credits with no monetary value. Entertainment purposes only. Skill-based challenges with predetermined rewards.
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="game"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="h-full"
            >
              {GameComponent && (
                <GameComponent
                  onWin={handleGameWin}
                  onBack={backToSelection}
                  walletAddress={walletAddress}
                  connected={connected}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
